import 'react-datepicker/dist/react-datepicker.css';

import { DateTime } from 'luxon';
import { getIn } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Flex, Text } from 'rebass';
import Select from 'react-select';
import { FaInfoCircle } from 'react-icons/fa';

import { TouchScrollable } from 'react-scrolllock';
import { checkIsIpadUser } from 'story-media/hooks';
import { TextFieldWrap, Label, ErrorMessageWrap } from '../text/styled';

const days = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: (i + 1).toString(),
}));

const months = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(0, i).toLocaleString('default', { month: 'short' }), // Full month name
}));

const currentYear = new Date().getFullYear();

const years = Array.from({ length: 121 }, (_, i) => ({
  value: currentYear - i,
  label: (currentYear - i).toString(),
}));
years.unshift({
  value: currentYear + 1,
  label: (currentYear + 1).toString(),
});


export const customStyles = {
  container: (base) => ({
    ...base,
    minWidth: '70px',
    marginRight: "5px",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9,
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    padding: 20,
    backgroundColor: isSelected ? '#9B59B6' : 'white',
    width: '100%',
  }),
  control: (base, { isFocused }) => ({
    display: 'flex',
    border: isFocused ? '1px solid #9B59B6' : '1px solid #D1D1D1',
    borderRadius: '31px',
    background: 'white',
    boxShadow: '0px 1px 2px 0px rgba(10, 13, 20, 0.03);',
    height: '42px',
    width: '100%',

  }),
  valueContainer: base => ({
    ...base,
    padding: '12px 8px',
    width: '100%',
  }),
  singleValue: base => ({
    ...base,
    fontFamily: 'Poppins',
    fontSize: '14px',
    letterSpacing: '-0.084px',
    color: '#666',
    width: '100%',
  }),
};

// react-select renders its dropdown menu INLINE. Inside the app modal (e.g. "Add a
// family member", "Edit header") react-scrolllock's TouchScrollable preventDefault()s
// touchmove on everything that isn't itself scrollable — which stops the day/month/year
// option lists from scrolling on touch. (On non-modal screens like "Create a story"
// there's no scroll-lock, so it worked there.) Wrapping the menu list in its OWN
// TouchScrollable makes react-scrolllock allow touch scrolling inside it. The function-
// child form lets us keep react-select's own innerRef alongside TouchScrollable's ref.
// Touch-only — desktop wheel scrolling is unaffected.
const TouchMenuList = (props) => {
  const { children, getStyles, innerRef, innerProps, maxHeight } = props;
  const menuListStyles = (typeof getStyles === 'function' && getStyles('menuList', props)) || {};
  return (
    <TouchScrollable>
      {(scrollableRef) => (
        <div
          ref={(node) => {
            scrollableRef(node);
            if (typeof innerRef === 'function') innerRef(node);
            else if (innerRef) innerRef.current = node;
          }}
          style={{
            ...menuListStyles,
            maxHeight: menuListStyles.maxHeight || maxHeight || 300,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
          {...innerProps}
        >
          {children}
        </div>
      )}
    </TouchScrollable>
  );
};

const DateField = ({
  field,
  form,
  placeholder,
  label,
  showError,
  minDate,
  maxDate,
  pickerProps,
  subText,
  menuPlacement = "bottom",
  ...props
}) => {
  const [day, setDay] = useState(null);
  const [month, setMonth] = useState(null);
  const [year, setYear] = useState(null);

  const error = getIn(form.errors, field.name);
  const touched = getIn(form.touched, field.name);

  const isIpadUser = checkIsIpadUser();

  // iPad-only menu hardening (react-select v2). On iPad the inline dropdown menu
  // (a) opened downward and fell off the bottom of the screen, and (b) was hard to
  // scroll because iOS momentum scrolling isn't enabled on the option list.
  // Fix without a portal (v2's menuPortal + fixed breaks touch-open on iPad):
  //   - `menuPlacement: 'auto'` flips the menu above the control when there's no room
  //     below, so it stays on screen.
  //   - cap the list height and enable touch scrolling.
  // Desktop keeps the original inline menu, placement, and styles untouched.
  const selectStyles = isIpadUser
    ? {
      ...customStyles,
      menuList: (base) => ({
        ...base,
        maxHeight: '40vh',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
      }),
    }
    : customStyles;

  const resolvedMenuPlacement = isIpadUser ? 'auto' : menuPlacement;

  useEffect(() => {
    if (!field.value) return;
    // Parse in UTC so the day/month/year match exactly what the BE sent,
    // regardless of the browser's local timezone.
    const dt = DateTime.fromISO((field.value ?? '').toString().split('T')[0]);

    if (dt.isValid) {
      setDay(dt.day);
      setMonth(dt.month);
      setYear(dt.year);
    }
  }, [field.value])

  useEffect(() => {
    if (day && month && year)
      updateDate()

  }, [day, month, year])

  const handleDayChange = (e) => {
    const selectedDay = Number(e.value);
    setDay(selectedDay);
  };

  const handleMonthChange = (e) => {
    const selectedMonth = e.value;
    setMonth(selectedMonth);
  };

  const handleYearChange = (e) => {
    const selectedYear = Number(e.value);
    setYear(selectedYear);
  };

  const updateDate = () => {
    form.setFieldValue(field.name, DateTime.utc(year, month, day).toISODate());
  };

  return (
    <TextFieldWrap mb={2} error={error && touched} {...props}>
      {label && <Label {...field}>{label}</Label>}
      <Flex>
        <Select
          className="react-select-container"
          menuPlacement={resolvedMenuPlacement}
          options={days}
          value={day ? { label: day, value: day } : undefined}
          onChange={handleDayChange}
          styles={selectStyles}
          placeholder="DD"
          isSearchable={false}
          components={{
            IndicatorSeparator: () => null,
            MenuList: TouchMenuList,
          }}
        />

        <Select
          className="react-select-container"
          menuPlacement={resolvedMenuPlacement}
          options={months}
          value={month ? months[month - 1] : undefined}
          onChange={handleMonthChange}
          styles={selectStyles}
          placeholder="MM"
          isSearchable={false}
          components={{
            IndicatorSeparator: () => null,
            MenuList: TouchMenuList,
          }}
        />

        <Select
          className="react-select-container"
          menuPlacement={resolvedMenuPlacement}
          options={years}
          value={year ? { label: year, value: year } : undefined}
          onChange={handleYearChange}
          styles={selectStyles}
          placeholder="YYYY"
          isSearchable={false}
          components={{
            IndicatorSeparator: () => null,
            MenuList: TouchMenuList,
          }}
        />
      </Flex>
      {subText && <Flex mt="4px">
        <Text as={FaInfoCircle} color="#B0B0B0" size={14} minWidth={14} />
        <Text
          ml={1}
          sx={{
            color: '#B0B0B0',
            fontFeatureSettings: "'liga' off, 'calt' off",
            fontFamily: 'Poppins',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '16px',
          }}
        >{subText}</Text>
      </Flex>}
      {error && touched && <ErrorMessageWrap {...field}>{error}</ErrorMessageWrap>}
    </TextFieldWrap>
  );
};

DateField.propTypes = {
  field: PropTypes.shape({}).isRequired,
  form: PropTypes.shape({}).isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderRadius: PropTypes.number,
  fontSize: PropTypes.number,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  showError: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  pickerProps: PropTypes.shape({}),
};

DateField.defaultProps = {
  // width: 320,
  borderRadius: 6,
  fontSize: 16,
  label: '',
  placeholder: '',
  showError: false,
  minDate: null,
  maxDate: null,
  pickerProps: {},
};

export default DateField;
